import React, { useEffect, useState, useRef } from 'react'

import { InsufficientPermissions, PaginatedTable, PaginatedTableCallbacks } from '../../common'
import { SupplierCollectionsTable } from './collectionTable'
import { AssignSuppliersForm } from './assignSupplierForm'
import defineAbilityFor from './../defineAbility'
import { JsonSupplierCollectionsRow, JsonUser, SupplierCollectionsRow, User } from '../../../models'
import { getSupplierCollections, getUsers, getUnassignedSupplierCollectionsCount } from '../../../api'
import { Roles, getAuthUser, userHasRole } from '../../../helpers'
import { SupplierFilters } from './filters'
import { SuppliersAdvancedSearch } from '..'

export const SupplierCollectionsLanding = () => {
    const ability = defineAbilityFor(getAuthUser())
    const [collectionAgentUsers, setCollectionAgentUsers] = useState<User[]>([])
    const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([])
    const [assignedUserId, setAssignedUserId] = useState<string>('')
    const [sortBy, setSortBy] = useState('balance')
    const [sortDirection, setSortDirection] = useState('desc')
    const updateFiltersRef = useRef<(filterName: string, newValue: string | null) => void>()

    const canView = ability.can('View', 'SupplierCollections')
    const canAssign = ability.can('Assign', 'Supplier')

    const toggleSupplierSelection = (id: number) => {
        if (selectedSuppliers.includes(id)) {
            setSelectedSuppliers(selectedSuppliers.filter(supplierId => supplierId !== id))
        } else {
            setSelectedSuppliers([...selectedSuppliers, id])
        }
    }

    const handleSortChange = (field: string) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(field)
            setSortDirection('desc')
        }
    }

    useEffect(() => {
        getUsers()
            .then(json => {
                const usersData: JsonUser[] = json.data
                const parsedUsers: User[] = usersData.map((user: JsonUser) => new User(user))
                const collectionAgents: User[] = parsedUsers.filter(user => userHasRole(user, Roles.CollectionAgent))
                setCollectionAgentUsers(collectionAgents)
            })
            .catch(() => {})
    }, [])

    const getSupplierCollectionsByStatus = (params?: { page?: string }) =>
        getSupplierCollections({ ...params, sortBy, sortDirection })

    if (!canView) {
        return <InsufficientPermissions />
    }

    return (
        <PaginatedTable<SupplierCollectionsRow, JsonSupplierCollectionsRow>
            getData={(params) => getSupplierCollectionsByStatus(params)}
            modelConstructor={(json: JsonSupplierCollectionsRow) => new SupplierCollectionsRow(json)}
            Filters={(props) => {
                updateFiltersRef.current = props.updateFilters
                return <SupplierFilters {...props} />
            }}
            Form={
                canAssign
                    ? (props) => (
                        <div style={{ marginTop: '-18px' }}>
                            <AssignSuppliersForm
                                users={collectionAgentUsers}
                                {...props}
                                getSupplierCount={getUnassignedSupplierCollectionsCount}
                                selectedSuppliers={selectedSuppliers}
                                setSelectedSuppliers={setSelectedSuppliers}
                                assignedUserId={assignedUserId}
                                onAssignedUserIdChange={setAssignedUserId}
                                updateFilters={(filterName, value) => {
                                    if (updateFiltersRef.current) {
                                        updateFiltersRef.current(filterName, value)
                                    }
                                }}
                            />
                        </div>
                    )
                    : undefined
            }
            disableSearch={true}
            AdvancedSearchComponent={SuppliersAdvancedSearch}
            Table={(
                supplierCollections: SupplierCollectionsRow[],
                callbacks: PaginatedTableCallbacks
            ) => (
                <SupplierCollectionsTable
                    callbacks={callbacks}
                    resources={{ supplierCollections }}
                    selectedSuppliers={selectedSuppliers}
                    toggleSupplierSelection={toggleSupplierSelection}
                    users={collectionAgentUsers}
                    setSelectedSuppliers={setSelectedSuppliers} />
            )}
            sortOptions={{
                sortBy,
                sortDirection,
                onSortChange: handleSortChange,
                sortFields: [
                    { field: 'name', label: 'Name' },
                    { field: 'balance', label: 'Balance' }
                ]
            }}
            title="Supplier Collections"
        />
    )
}
